import { CountryCode, FlagComponent, Tooltip } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';

interface EntityCardProps {
  name: string | null;
  incorporationCountry: string;
  statusText?: string;
  link: string | null;
}

export const EntityCard = ({
  name,
  incorporationCountry,
  statusText,
  link,
}: EntityCardProps) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = nameRef.current;
    if (el) {
      setIsNameTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation, name]);

  const displayName = name || `New ${incorporationCountry} Entity`;

  const nameElement = (
    <span
      ref={nameRef}
      className={cn(
        'truncate block w-full',
        name
          ? 'txt-extra-small-bold text-text-primary'
          : 'txt-extra-small-regular text-text-disabled'
      )}
    >
      {displayName}
    </span>
  );

  const content = (
    <>
      <FlagComponent
        _className="size-6 shrink-0"
        country={incorporationCountry as CountryCode}
        countryName={incorporationCountry}
      />
      <div className="flex flex-col flex-1 min-w-0 justify-center overflow-hidden">
        {isNameTruncated ? (
          <Tooltip content={displayName}>{nameElement}</Tooltip>
        ) : (
          nameElement
        )}
        {statusText && (
          <span className="txt-extra-small-regular text-text-secondary whitespace-nowrap">
            {statusText}
          </span>
        )}
      </div>
    </>
  );

  if (link) {
    return (
      <Link
        href={link}
        className="group flex-1 min-w-0 flex items-center gap-md p-lg border border-border-secondary rounded-xl shadow-xs bg-bg-primary hover:bg-bg-secondary transition-colors duration-150 ease-out"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex items-center gap-md p-lg border border-border-secondary rounded-xl shadow-xs bg-bg-primary">
      {content}
    </div>
  );
};
